import { useState } from 'react';
import { useFormikContext } from 'formik';
import { CVBuilderFormValues } from 'utils/cv-form-db';
import { useCvBuilderData } from '../data/CvBuilderDataContext';

interface DeleteModalState {
  isOpen: boolean;
  indexToDelete: number | null;
}

interface UseCVStepActionOptions<T> {
  fieldName: keyof CVBuilderFormValues;
  sortFunction?: (a: T, b: T) => number;
  disableAutoSave?: boolean;
}

export const useCVStepActions = <T>({
  fieldName,
  sortFunction,
  disableAutoSave = false
}: UseCVStepActionOptions<T>) => {
  const { values, setFieldValue } = useFormikContext<CVBuilderFormValues>();
  const { saveDraft } = useCvBuilderData();

  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const [deleteModalState, setDeleteModalState] = useState<DeleteModalState>({
    isOpen: false,
    indexToDelete: null
  });

  const isEditing = editingIndex !== null;
  const isFormOpen = isAdding || isEditing;
  const currentItems = (values[fieldName] as T[]) || [];
  const editingItem =
    isEditing && editingIndex !== null ? currentItems[editingIndex] : undefined;

  const saveData = async (updatedItems: T[]) => {
    await setFieldValue(fieldName, updatedItems);
    if (!disableAutoSave) {
      // Fire-and-forget: don't block the list UI on the background sync (the port
      // handles persistence + errors and never rejects).
      void saveDraft({ ...values, [fieldName]: updatedItems });
    }
  };

  const handleAdd = async (newItem: T) => {
    const updatedItems = [...currentItems, newItem];
    const sortedItems = sortFunction
      ? updatedItems.sort(sortFunction)
      : updatedItems;

    await saveData(sortedItems);
    setIsAdding(false);
  };

  const handleEdit = async (updatedItem: T) => {
    if (editingIndex === null) return;

    const itemList = [...currentItems];
    itemList[editingIndex] = updatedItem;
    const sortedItems = sortFunction ? itemList.sort(sortFunction) : itemList;

    await saveData(sortedItems);
    setEditingIndex(null);
  };

  const handleDeleteClick = (index: number) => {
    setDeleteModalState({
      isOpen: true,
      indexToDelete: index
    });
  };

  const handleConfirmDelete = async () => {
    if (deleteModalState.indexToDelete === null) return;

    const updatedItems = currentItems.filter(
      (_, i) => i !== deleteModalState.indexToDelete
    );
    await saveData(updatedItems);
    setDeleteModalState({ isOpen: false, indexToDelete: null });
  };

  const handleCancelDelete = () => {
    setDeleteModalState({ isOpen: false, indexToDelete: null });
  };

  const handleEditClick = (index: number) => {
    setEditingIndex(index);
    setIsAdding(false);
  };

  const handleAddClick = () => {
    setIsAdding(true);
    setEditingIndex(null);
  };

  const handleCancelForm = () => {
    setIsAdding(false);
    setEditingIndex(null);
  };

  return {
    isAdding,
    isEditing,
    isFormOpen,
    editingIndex,
    editingItem,
    deleteModalState,
    currentItems,
    handleAdd,
    handleEdit,
    handleDeleteClick,
    handleConfirmDelete,
    handleCancelDelete,
    handleEditClick,
    handleAddClick,
    handleCancelForm,
    setIsAdding,
    setEditingIndex,
    setDeleteModalState
  };
};
